import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import {
  GlobalAttributesDocument,
  SpacesDocument,
  usePromoteAttributeToGlobalMutation,
} from "#graphql/supergraphHooks";
import {
  type PromoteAttributeToGlobalMutation,
  type PromoteAttributeToGlobalMutationVariables,
} from "#graphql/supergraphTypes";

export interface UsePromoteAttributeToGlobalReturn {
  /**
   * Promote one space-local attribute. `label` renames it as part of the
   * promotion; omit it to keep the stored label.
   */
  promoteAttributeToGlobal: (
    attributeId: string,
    label?: string,
  ) => Promise<PromoteAttributeToGlobalMutation["promoteAttributeToGlobal"] | null>;
  loading: boolean;
  error: ReturnType<typeof usePromoteAttributeToGlobalMutation>[1]["error"];
}

/**
 * DV-648 — lift a space-local attribute into the global library (the confirm
 * step of the DV-499 promotion pre-check modal). An in-place kind flip
 * server-side, so documents already tagged with the attribute keep resolving.
 *
 * Refetches **both** catalogs, because one promotion changes both sides of the
 * settings screen: the attribute joins `GlobalAttributes` and leaves its
 * Space's local list in `Spaces`. Every read here is `no-cache`, so there is no
 * normalized cache that would update on its own.
 *
 * Rejects rather than resolving on a server refusal — notably
 * `ATTRIBUTE_LABEL_CONFLICT`, which the mutation enforces regardless of what
 * the advisory pre-check displayed (see `usePromotionPreflight`). Callers are
 * expected to keep their modal open on rejection.
 */
export const usePromoteAttributeToGlobal = (
  options?: MutationHookOptions<
    PromoteAttributeToGlobalMutation,
    PromoteAttributeToGlobalMutationVariables
  >,
): UsePromoteAttributeToGlobalReturn => {
  const [mutate, { loading, error }] = usePromoteAttributeToGlobalMutation({
    refetchQueries: [GlobalAttributesDocument, SpacesDocument],
    awaitRefetchQueries: true,
    ...options,
  });

  const promoteAttributeToGlobal = useCallback(
    async (attributeId: string, label?: string) => {
      const { data } = await mutate({
        // Omitted rather than sent as null: an absent `label` is the server's
        // "keep the stored one", while an explicit null would fail validation.
        variables: { id: attributeId, ...(label !== undefined ? { label } : {}) },
      });
      return data?.promoteAttributeToGlobal ?? null;
    },
    [mutate],
  );

  return { promoteAttributeToGlobal, loading, error };
};
